import React, { useMemo } from "react"

import { Tooltip } from "@coval/ui"

import { HighlightedText } from "@/components/HighlightText"
import {
  TemplateVariableInput,
  type TemplateEntityValues,
} from "@/components/shared/TemplateVariableInput"
import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

interface ExpandableTemplateVariableTextProps {
  value: string
  onChange?: (value: string) => void
  isExpanded: boolean
  onExpand?: () => void
  placeholder?: string
  entityAttributes?: Record<string, string[]>
  entityValues?: TemplateEntityValues
  className?: string
  maxCollapsedLength?: number
  showTooltip?: boolean
  highlightText?: string
  isHighlighted?: boolean
  autoFocus?: boolean
}

/**
 * Reusable component that shows template variables with replacement in collapsed state
 * and TemplateVariableInput in expanded state
 */
export const ExpandableTemplateVariableText: React.FC<
  ExpandableTemplateVariableTextProps
> = ({
  value,
  onChange,
  isExpanded,
  onExpand,
  placeholder = "",
  entityAttributes = {},
  entityValues = {},
  className = "",
  maxCollapsedLength = 100,
  showTooltip = true,
  highlightText,
  isHighlighted = false,
  autoFocus = false,
}) => {
  // Replace template variables in collapsed view
  const replacedTextParts = useMemo(() => {
    const parts: Array<{
      text: string
      entity?: string
      originalTemplate?: string
    }> = []
    let lastIndex = 0
    const regex = /\{\{(\w+)\.(\w+)\}\}/g
    let match

    while ((match = regex.exec(value)) !== null) {
      if (match.index > lastIndex) {
        parts.push({
          text: value.substring(lastIndex, match.index),
        })
      }

      const entity = match[1]
      const attribute = match[2]
      const entityData = entityValues[entity]

      if (entityData?.[attribute] !== undefined) {
        parts.push({
          text: stringifyUnknownValue(entityData[attribute]),
          entity,
          originalTemplate: match[0],
        })
      } else {
        parts.push({
          text: match[0],
        })
      }

      lastIndex = regex.lastIndex
    }

    if (lastIndex < value.length) {
      parts.push({
        text: value.substring(lastIndex),
      })
    }

    return parts.length > 0 ? parts : [{ text: value }]
  }, [value, entityValues])

  const displayText = useMemo(() => {
    return replacedTextParts.map((p) => p.text).join("")
  }, [replacedTextParts])

  // Use the replaced text parts but truncate if needed
  const truncatedParts = useMemo(() => {
    if (displayText.length <= maxCollapsedLength) {
      return replacedTextParts
    }

    // Truncate the parts
    const parts: Array<{
      text: string
      entity?: string
      originalTemplate?: string
    }> = []
    let currentLength = 0

    for (const part of replacedTextParts) {
      if (currentLength + part.text.length <= maxCollapsedLength) {
        parts.push(part)
        currentLength += part.text.length
      } else {
        // Add partial text
        const remaining = maxCollapsedLength - currentLength
        if (remaining > 0) {
          parts.push({
            text: part.text.substring(0, remaining),
            entity: part.entity,
            originalTemplate: part.originalTemplate,
          })
        }
        break
      }
    }

    if (displayText.length > maxCollapsedLength) {
      parts.push({ text: "..." })
    }

    return parts
  }, [replacedTextParts, displayText, maxCollapsedLength])

  // Collapsed view
  if (!isExpanded) {
    return (
      <span
        className={`${className} ${onExpand ? "cursor-pointer" : ""}`}
        onClick={onExpand}
      >
        {truncatedParts.map((part, index) => {
          let colorClass = ""
          if (part.entity === "agent") {
            colorClass = "text-blue-600 dark:text-blue-400"
          } else if (part.entity === "test_case") {
            colorClass = "text-green-600 dark:text-green-400"
          }

          const content = highlightText ? (
            <HighlightedText
              key={index}
              text={part.text}
              searchText={highlightText}
              isCurrentMatch={isHighlighted}
              className={colorClass}
            />
          ) : (
            <span key={index} className={colorClass}>
              {part.text}
            </span>
          )

          // Show tooltip with original template variable when hovering over replaced value
          if (part.originalTemplate && showTooltip) {
            return (
              <Tooltip key={index} content={part.originalTemplate} side="top">
                {content}
              </Tooltip>
            )
          }

          return content
        })}
      </span>
    )
  }

  // Expanded view
  if (!onChange) {
    // Read-only expanded view
    return (
      <div className={className}>
        {replacedTextParts.map((part, index) => {
          let colorClass = ""
          if (part.entity === "agent") {
            colorClass = "text-blue-600 dark:text-blue-400"
          } else if (part.entity === "test_case") {
            colorClass = "text-green-600 dark:text-green-400"
          }

          const content = highlightText ? (
            <HighlightedText
              key={index}
              text={part.text}
              searchText={highlightText}
              isCurrentMatch={isHighlighted}
              className={colorClass}
            />
          ) : (
            <span key={index} className={colorClass}>
              {part.text}
            </span>
          )

          if (part.originalTemplate && showTooltip) {
            return (
              <Tooltip key={index} content={part.originalTemplate} side="top">
                {content}
              </Tooltip>
            )
          }

          return content
        })}
      </div>
    )
  }

  // Editable expanded view
  // Use key to force remount when expanding to ensure height adjustment happens
  return (
    <TemplateVariableInput
      key={isExpanded ? "expanded" : "collapsed"}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      chipClassName="text-xs"
      entityAttributes={entityAttributes}
      entityValues={entityValues}
      highlightText={highlightText}
      isHighlighted={isHighlighted}
      autoFocus={autoFocus}
    />
  )
}
